import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { Box, Flex, TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { CompactAlert } from '~/components/common/CompactAlert'
import { routePaths } from '~/routes/routePaths'

/**
 * 取引可能エリア 0 件時に spot / intraday 両画面の上部に表示するバナー。
 *
 * 取引を行うには JEPX会員情報管理 / 広域マスタ の管理画面設定が必要。
 *
 * 取得失敗 (isError) 時は親コンポーネントがこのコンポーネントを render しないこと
 * (= data === undefined の場合は全エリア有効で運用する)。
 */

/**
 * 広域機関の画面へ移る導線。 UI 辞書 components/alert-placement.md §4「Alert の中の導線」:
 * 本文の下の文字リンクにせず、 Alert の右端に Tertiary sm で置く (`AlertActions` が
 * marginLeft auto で右端へ寄せる。 DS 0.14.2 の Alert に右寄せの action slot が無いため)。
 *
 * JEPX会員情報管理は閲覧専用になった (登録は運営側オペレーション、
 * `docs/specs/admin/integration/jepx-member-info.md`) ため導線から外し、
 * お客様が自分で設定できる広域マスタだけを残す。
 */
const OcctoMasterAction = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <AlertActions>
      <TertiaryButton asChild size="sm">
        <RouterLink to={routePaths.adminOccto.path}>
          {t('common:route.adminOccto')}
        </RouterLink>
      </TertiaryButton>
    </AlertActions>
  )
}

/**
 * バナーの**中身だけ**。 スポット市場の上部ステータス帯は同じ status の警告を
 * 1 枚の Alert にまとめて箇条書きにするため (デザイナー指示 2026-08-31)、
 * `Alert` の器を持たない形が要る。 時間前入札は帯を持たないので器付きの
 * [[TradableAreasBanner]] をそのまま使う。
 *
 * 箇条書きの項目ごとに行き先が違うので、 導線はこの項目の右端に置く (本文の下に導線の行を作らない)。
 */
export const TradableAreasBannerBody = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Flex alignItems="center" flex={1} gap={3}>
      <Box flex={1} minWidth={0}>
        <Text fontWeight="semibold">
          {t('common:bidding.tradableAreaWarning.title')}
        </Text>
        <Text marginTop={1} fontSize="sm">
          {t('common:bidding.tradableAreaWarning.configBody')}
        </Text>
      </Box>
      <OcctoMasterAction />
    </Flex>
  )
}

/**
 * 器付き (= 単独で 1 枚の通知として出す画面用)。 Compact Alert (alert-placement.md §0): 見出しと
 * 説明を 1 つの流れに置き、 導線は右端の Tertiary sm (§4)。
 */
export const TradableAreasBanner = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <CompactAlert
      actions={
        <TertiaryButton asChild size="sm">
          <RouterLink to={routePaths.adminOccto.path}>
            {t('common:route.adminOccto')}
          </RouterLink>
        </TertiaryButton>
      }
      description={t('common:bidding.tradableAreaWarning.configBody')}
      status="warning"
      title={t('common:bidding.tradableAreaWarning.title')}
    />
  )
}
